import { createSlice } from "@reduxjs/toolkit";

import axios from "../../utils/axios";
import utils from "../../utils/utils";
import { dispatch } from "../index";

const initialState = {
  nutritions: [],
  nutritionObject: {},
  error: null,
  loading: false,
};

const normalizeCreateDate = (value) => {
  if (Array.isArray(value) && value.length >= 3) {
    const date = new Date(
      Date.UTC(
        value[0],
        value[1] - 1,
        value[2],
        value[3] || 0,
        value[4] || 0,
        value[5] || 0,
        Math.floor((value[6] || 0) / 1000000)
      )
    );

    return Number.isNaN(date.getTime()) ? "" : date.toISOString();
  }

  return value || "";
};

const normalizeNutrition = (item = {}) => ({
  ...item,
  id: String(item.id || item._id || "").trim(),
  name: item.name || "",
  create_date: normalizeCreateDate(item.create_date),
  ingredients: Array.isArray(item.ingredients) ? item.ingredients : [],
  instructions: Array.isArray(item.instructions) ? item.instructions : [],
});

const sortNutritions = (items = []) =>
  [...items].sort((a, b) => {
    const featuredDelta = Number(!!b.featured) - Number(!!a.featured);
    if (featuredDelta !== 0) {
      return featuredDelta;
    }

    return String(a.id || "").localeCompare(String(b.id || ""));
  });

const mergeNutritions = (existing = [], incoming = []) => {
  const merged = {};

  for (const item of existing) {
    if (!item?.id) {
      continue;
    }

    merged[item.id] = normalizeNutrition(item);
  }

  for (const item of incoming) {
    if (!item?.id) {
      continue;
    }

    merged[item.id] = normalizeNutrition({
      ...(merged[item.id] || {}),
      ...item,
    });
  }

  return sortNutritions(Object.values(merged));
};

const slice = createSlice({
  name: "nutrition",
  initialState,
  reducers: {
    hasError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    startLoading(state) {
      state.loading = true;
    },
    stopLoading(state) {
      state.loading = false;
    },
    setSuccess(state, action) {
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      state.nutritions = sortNutritions(incoming.map(normalizeNutrition));
      state.nutritionObject = utils.getUniqueObject(state.nutritions, "id");
      state.loading = false;
    },
    mergeSuccess(state, action) {
      const incoming = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      state.nutritions = mergeNutritions(state.nutritions, incoming);
      state.nutritionObject = utils.getUniqueObject(state.nutritions, "id");
      state.loading = false;
    },
    deleteSuccess(state, action) {
      state.nutritions = state.nutritions.filter(
        (item) => item.id !== action.payload.id
      );
      state.nutritionObject = utils.getUniqueObject(state.nutritions, "id");
      state.loading = false;
    },
  },
});

export default slice.reducer;

export function startLoading() {
  return async () => {
    dispatch(slice.actions.startLoading());
  };
}

export function stopLoading() {
  return async () => {
    dispatch(slice.actions.stopLoading());
  };
}

export function getNutritions() {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const result = await axios.get("/uni/get-all-nutrition?page=0&size=100000");
      const nutritions = result?.data?.nutrition || [];
      dispatch(slice.actions.setSuccess(nutritions));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getNutritionById(
  id = "",
  callback = () => {},
  errCallback = () => {}
) {
  return async () => {
    const trimmedId = id.trim();

    if (!trimmedId) {
      errCallback(new Error("Nutrition id is required"));
      return;
    }

    dispatch(slice.actions.startLoading());

    try {
      const result = await axios.get(
        `/uni/get-nutrition?id=${encodeURIComponent(trimmedId)}`
      );
      const nutrition = normalizeNutrition(result?.data || {});
      dispatch(slice.actions.mergeSuccess(nutrition));
      callback(nutrition);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      errCallback(error);
    }
  };
}

export function saveNutrition(
  data,
  callback = () => {},
  errCallback = () => {}
) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const payload = {
        ...data,
        create_date:
          data?.create_date ||
          (!data?.id ? new Date().toISOString() : undefined),
      };
      const result = await axios.post("/uni/create-or-edit-nutrition", payload);
      console.log(result);
      const nutrition = normalizeNutrition(result?.data || {});
      dispatch(slice.actions.mergeSuccess(nutrition));
      callback(nutrition);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      errCallback(error);
    }
  };
}

export function purgeUnusedNutrition(
  callback = () => {},
  errCallback = () => {}
) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const result = await axios.post("/uni/purge-unused-nutrition?async=true");
      dispatch(slice.actions.stopLoading());
      callback(result?.data || {});
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      errCallback(error);
    }
  };
}

export function getUnusedNutritionPurgeProgress(
  jobId = "",
  callback = () => {},
  errCallback = () => {}
) {
  return async () => {
    try {
      const result = await axios.get(
        `/uni/purge-unused-nutrition-progress?job_id=${encodeURIComponent(
          jobId
        )}`
      );
      callback(result?.data || {});
    } catch (error) {
      errCallback(error);
    }
  };
}

export function deleteNutrition(
  id = "",
  callback = () => {},
  errCallback = () => {}
) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      await axios.delete(`/uni/delete-nutrition?id=${encodeURIComponent(id)}`);
      dispatch(slice.actions.deleteSuccess({ id }));
      callback();
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      errCallback(error);
    }
  };
}
